"use client";

import { useAuth } from "@/features/auth/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { ArrowLeft, Plus, Trash2, Edit2, Briefcase } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ExperienceItem, WorkExperienceData } from "../types";
import { normalizeWorkExperience } from "../utils";
import { ProfileStepsLayout, Step } from "../components/ProfileStepsLayout";

interface TalentExperienceProps {
  currentStep: number;
  totalSteps: number;
}

export default function TalentExperience({
  currentStep,
  totalSteps
}: TalentExperienceProps) {
  const { t } = useTranslation(["talent", "common"]);
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [areasOfExpertise, setAreasOfExpertise] = useState("");
  const [experiences, setExperiences] = useState<ExperienceItem[]>([]);
  const [initialExperiences, setInitialExperiences] = useState<
    ExperienceItem[]
  >([]);
  const [initialAreasOfExpertise, setInitialAreasOfExpertise] = useState("");

  // Step information
  const stepTitle = t("talent:experience.title");
  const stepDescription = t("talent:experience.description");
  const estimatedTime = t("talent:application.estimated_times.10_minutes");

  // Form State for new experience
  const [newExperience, setNewExperience] = useState<Partial<ExperienceItem>>({
    company: "",
    role: "",
    startDate: "",
    endDate: "",
    isCurrent: false,
    description: ""
  });

  // Ensure we don't have undefined values
  const [editingId, setEditingId] = useState<string | null>(null);

  const getStepStatus = (
    stepId: string
  ): "completed" | "current" | "upcoming" => {
    // Basic logic for visual steps, relying on actual data is better but for now strictly visual alignment
    if (stepId === "experience") return "current";
    if (stepId === "education") return "upcoming";
    return "completed"; // Previous steps are completed to get here
  };

  const steps: Step[] = [
    {
      id: "personal_info",
      title: t("talent:layout.progress_steps.personal_info"),
      href: "/profile/personal-info",
      status: getStepStatus("personal_info")
    },
    {
      id: "german_skills",
      title: t("talent:dashboard.progress.steps.german_skills"),
      href: "/profile/german-skills",
      status: getStepStatus("german_skills")
    },
    {
      id: "experience",
      title: t("talent:dashboard.progress.steps.experience"),
      href: "/profile/experience",
      status: getStepStatus("experience")
    },
    {
      id: "education",
      title: t("talent:dashboard.progress.steps.education"),
      href: "/profile/education",
      status: getStepStatus("education")
    }
  ];

  const lastFetchedProfileId = useRef<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!profile?.id) return;
      if (lastFetchedProfileId.current === profile.id) return;

      try {
        lastFetchedProfileId.current = profile.id;
        const { data, error } = await supabase
          .from("talent_profiles")
          .select("work_experience")
          .eq("id", profile.id)
          .single();

        if (error) throw error;

        if (data?.work_experience) {
          const normalized = normalizeWorkExperience(data.work_experience);
          setExperiences(normalized.entries);
          setInitialExperiences(normalized.entries);
          setAreasOfExpertise(normalized.areas_of_expertise);
          setInitialAreasOfExpertise(normalized.areas_of_expertise);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast({
          title: t("common:status.error"),
          description: t("talent:profile.load_error"),
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profile?.id, t, toast]);

  const validateNewExperience = () => {
    if (
      !newExperience.company ||
      !newExperience.role ||
      !newExperience.startDate ||
      (!newExperience.isCurrent && !newExperience.endDate) ||
      !newExperience.description
    ) {
      return false;
    }
    return true;
  };

  const handleAddExperience = () => {
    if (!validateNewExperience()) {
      toast({
        title: t("common:status.error"),
        description: t("talent:experience.validation_error"),
        variant: "destructive"
      });
      return;
    }

    if (editingId) {
      // Update existing experience
      setExperiences((prev) =>
        prev.map((exp) =>
          exp.id === editingId
            ? {
                ...exp,
                company: newExperience.company!,
                role: newExperience.role!,
                startDate: newExperience.startDate!,
                endDate: newExperience.isCurrent
                  ? null
                  : newExperience.endDate!,
                isCurrent: newExperience.isCurrent || false,
                description: newExperience.description!
              }
            : exp
        )
      );
      setEditingId(null);
    } else {
      // Add new experience
      const experience: ExperienceItem = {
        id: crypto.randomUUID(),
        company: newExperience.company!,
        role: newExperience.role!,
        startDate: newExperience.startDate!,
        endDate: newExperience.isCurrent ? null : newExperience.endDate!,
        isCurrent: newExperience.isCurrent || false,
        description: newExperience.description!
      };
      setExperiences([...experiences, experience]);
    }

    setNewExperience({
      company: "",
      role: "",
      startDate: "",
      endDate: "",
      isCurrent: false,
      description: ""
    });
  };

  const handleEdit = (experience: ExperienceItem) => {
    setNewExperience({
      company: experience.company,
      role: experience.role,
      startDate: experience.startDate,
      endDate: experience.endDate || "",
      isCurrent: experience.isCurrent,
      description: experience.description
    });
    setEditingId(experience.id);
  };

  const handleCancelEdit = () => {
    setNewExperience({
      company: "",
      role: "",
      startDate: "",
      endDate: "",
      isCurrent: false,
      description: ""
    });
    setEditingId(null);
  };

  const handleRemoveExperience = (id: string) => {
    if (editingId === id) {
      handleCancelEdit();
    }
    setExperiences(experiences.filter((exp) => exp.id !== id));
  };

  const handleSave = async () => {
    const finalExperiences = [...experiences];
    const isFormDirty =
      newExperience.company ||
      newExperience.role ||
      newExperience.startDate ||
      newExperience.endDate ||
      newExperience.description;

    if (isFormDirty) {
      if (validateNewExperience()) {
        if (editingId) {
          const index = finalExperiences.findIndex(
            (exp) => exp.id === editingId
          );
          if (index !== -1) {
            finalExperiences[index] = {
              ...finalExperiences[index],
              id: editingId,
              company: newExperience.company!,
              role: newExperience.role!,
              startDate: newExperience.startDate!,
              endDate: newExperience.isCurrent ? null : newExperience.endDate!,
              isCurrent: newExperience.isCurrent || false,
              description: newExperience.description!
            };
          }
        } else {
          const experience: ExperienceItem = {
            id: crypto.randomUUID(),
            company: newExperience.company!,
            role: newExperience.role!,
            startDate: newExperience.startDate!,
            endDate: newExperience.isCurrent ? null : newExperience.endDate!,
            isCurrent: newExperience.isCurrent || false,
            description: newExperience.description!
          };
          finalExperiences.push(experience);
        }
        handleCancelEdit();
      } else {
        toast({
          title: t("common:status.warning"),
          description: t("talent:experience.validation_error"),
          variant: "default"
        });
        return;
      }
    }

    if (
      JSON.stringify(finalExperiences) === JSON.stringify(initialExperiences) &&
      areasOfExpertise === initialAreasOfExpertise
    ) {
      if (!areasOfExpertise.trim()) {
        toast({
          title: t("common:status.error"),
          description: t(
            "talent:experience.validation.areas_of_expertise_required",
            "Areas of expertise is required"
          ),
          variant: "destructive"
        });
        return;
      }
      navigate("/profile/education");
      return;
    }

    if (!areasOfExpertise.trim()) {
      toast({
        title: t("common:status.error"),
        description: t(
          "talent:experience.validation.areas_of_expertise_required",
          "Areas of expertise is required"
        ),
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      const payload: WorkExperienceData = {
        entries: finalExperiences,
        areas_of_expertise: areasOfExpertise
      };

      const { error } = await supabase
        .from("talent_profiles")
        .update({
          work_experience: payload
        })
        .eq("id", profile.id);

      if (error) throw error;

      toast({
        title: t("common:status.success"),
        description: t("talent:experience.save_success")
      });

      navigate("/profile/education");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: t("common:status.error"),
        description: t("talent:experience.save_error"),
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <ProfileStepsLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={stepTitle}
      description={stepDescription}
      estimatedTime={estimatedTime}
      steps={steps}
    >
      <div className="space-y-6">
        {experiences.length > 0 && (
          <div className="space-y-4">
            {experiences.map((exp) => (
              <Card key={exp.id} data-testid={`experience-item-${exp.id}`}>
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                  <div>
                    <CardTitle className="text-lg font-semibold">
                      {exp.role}
                    </CardTitle>
                    <CardDescription className="font-medium text-gray-700">
                      {exp.company}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(exp)}
                      className="text-muted-foreground hover:text-primary"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      id={`remove-experience-${exp.id}`}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveExperience(exp.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-gray-500 mb-2">
                    {new Date(exp.startDate).toLocaleDateString()} -{" "}
                    {exp.isCurrent
                      ? t("talent:experience.present")
                      : exp.endDate
                        ? new Date(exp.endDate).toLocaleDateString()
                        : ""}
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {exp.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-primary" />
              {editingId
                ? t("talent:experience.edit_title", "Edit Experience")
                : t("talent:experience.add_new_title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company">
                  {t("talent:experience.fields.company")} *
                </Label>
                <Input
                  id="company"
                  value={newExperience.company}
                  onChange={(e) =>
                    setNewExperience({
                      ...newExperience,
                      company: e.target.value
                    })
                  }
                  placeholder={t("talent:experience.placeholders.company")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">
                  {t("talent:experience.fields.role")} *
                </Label>
                <Input
                  id="role"
                  value={newExperience.role}
                  onChange={(e) =>
                    setNewExperience({ ...newExperience, role: e.target.value })
                  }
                  placeholder={t("talent:experience.placeholders.role")}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">
                  {t("talent:experience.fields.start_date")} *
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={newExperience.startDate}
                  onChange={(e) =>
                    setNewExperience({
                      ...newExperience,
                      startDate: e.target.value
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">
                  {t("talent:experience.fields.end_date")} *
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={newExperience.endDate || ""}
                  disabled={newExperience.isCurrent}
                  onChange={(e) =>
                    setNewExperience({
                      ...newExperience,
                      endDate: e.target.value
                    })
                  }
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isCurrent"
                checked={newExperience.isCurrent}
                onCheckedChange={(checked) =>
                  setNewExperience({
                    ...newExperience,
                    isCurrent: checked as boolean,
                    endDate: checked ? null : newExperience.endDate
                  })
                }
              />
              <Label htmlFor="isCurrent">
                {t("talent:experience.fields.is_current")}
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                {t("talent:experience.fields.description")} *
              </Label>
              <Textarea
                id="description"
                value={newExperience.description}
                onChange={(e) =>
                  setNewExperience({
                    ...newExperience,
                    description: e.target.value
                  })
                }
                placeholder={t("talent:experience.placeholders.description")}
                rows={4}
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              {editingId && (
                <Button variant="outline" onClick={handleCancelEdit}>
                  {t("common:buttons.cancel")}
                </Button>
              )}
              <Button
                id="add-experience-button"
                type="button"
                onClick={handleAddExperience}
                className="w-full md:w-auto"
                disabled={!validateNewExperience()}
              >
                <Plus className="w-4 h-4 mr-2" />
                {editingId
                  ? t("common:buttons.save")
                  : t("talent:experience.buttons.add")}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="expertise-card">
          <CardHeader>
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-primary" />
              {t("talent:experience.fields.areas_of_expertise")} *
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Textarea
                value={areasOfExpertise}
                onChange={(e) => setAreasOfExpertise(e.target.value)}
                placeholder={t(
                  "talent:experience.placeholders.areas_of_expertise"
                )}
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between pt-6 border-t">
          <Button
            variant="outline"
            onClick={() => navigate("/profile/german-skills")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common:buttons.back")}
          </Button>
          <Button id="next-button" onClick={handleSave} disabled={saving}>
            {saving && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            )}
            {t("common:buttons.next")}
          </Button>
        </div>
      </div>
    </ProfileStepsLayout>
  );
}
